import { sb } from '../supabase';
import { translateChatMessage } from './maabarAi/client';
import { detectTextLanguage, directionBetween } from './maabarAi/config';

// Auto-translation for the free-text a buyer typed into an in-chat card — the
// destination, the customisation, the packaging note. Not the card's labels:
// those are i18n and come from the component's own strings table.
//
// WHY THIS EXISTS SEPARATELY FROM THE MESSAGE PATH
// Chat.jsx renders a card by intercepting a message whose content is the literal
// "[request:<uuid>]" and handing the uuid to the component, which fetches the row
// itself. The translation effect in Chat.jsx only ever sees message.content, so
// it would translate the marker string and never the card. Cards are structurally
// outside that pipeline; this closes the gap.
//
// CACHE
// Reuses message_translations, keyed (message_id, direction) — the card belongs
// to exactly one message, so that key already identifies it uniquely and no new
// table is needed. Chat.jsx:115 isMediaMessage() excludes "[request:" and
// "[invoice:" from the ordinary text path, so nothing else ever writes that key
// and the two cannot collide.
//
// The stored value is a JSON object of field -> translated string, not prose.
// That is the one liberty taken with the column: it is the reason a reader gets
// the card instantly on a second open instead of paying for it again.

// Long enough for any real destination, customisation note or packaging line,
// short enough that one pasted specification sheet cannot turn into a large bill.
// Anything longer is sent truncated rather than dropped — a clipped translation
// beats none.
export const CARD_FIELD_CHAR_CAP = 2000;

const cap = (v) => String(v ?? '').slice(0, CARD_FIELD_CHAR_CAP);

// One call per card, not one per field. Five fields would be five round trips
// against a 150/hour budget for this task (maabar-ai RATE_LIMITS.chat_translation),
// which a couple of busy threads would exhaust. Newline-joined keeps it to one.
const JOIN = '\n';

/**
 * Translate a card's free-text fields into the reader's language.
 *
 * @param {object}  args
 * @param {string}  args.messageId  the [request:]/[invoice:] message's id — the cache key
 * @param {object}  args.fields     { key: sourceText } ; empty values are skipped
 * @param {string}  args.readerLang the reader's UI language ('ar'|'en'|'zh')
 * @returns {Promise<object>} { key: translatedText } — only keys that changed.
 *                            Always resolves; never throws into a render.
 */
export async function translateCardFields({ messageId, fields = {}, readerLang = 'ar' }) {
  const entries = Object.entries(fields).filter(([, v]) => String(v ?? '').trim());
  if (!entries.length || !messageId || String(messageId).startsWith('temp-')) return {};

  // One direction for the whole card, taken from the longest field: mixed-script
  // cards are rare, and the longest value is the most reliable sample.
  const longest = entries.reduce((a, b) => (String(b[1]).length > String(a[1]).length ? b : a));
  const sourceLang = detectTextLanguage(longest[1]);
  const direction = directionBetween(sourceLang, readerLang);
  if (direction === 'off') return {};              // already in the reader's language

  // 1. Cache.
  try {
    const { data } = await sb.from('message_translations')
      .select('translated_text')
      .eq('message_id', messageId)
      .eq('direction', direction)
      .maybeSingle();
    if (data?.translated_text) {
      const cached = JSON.parse(data.translated_text);
      // Only trust the cache when it covers every field being asked for now; a
      // card edited after its first translation falls through and re-translates.
      if (cached && entries.every(([k]) => typeof cached[k] === 'string')) return cached;
    }
  } catch { /* a miss, a parse failure and an RLS denial are all just "translate it" */ }

  // 2. Translate — one request, newline-joined, same maabar-ai path messages use.
  let out;
  try {
    const joined = entries.map(([, v]) => cap(v)).join(JOIN);
    const translated = await translateChatMessage({
      text: joined,
      sourceLanguage: sourceLang,
      targetLanguage: readerLang,
      conversationRole: 'trade_chat',
    });
    const lines = String(translated || '').split(JOIN);
    // The model is instructed to add nothing, so the line count should survive.
    // If it does not, we cannot tell which line belongs to which field — show the
    // originals rather than risk labelling the destination with the packaging.
    if (lines.length !== entries.length) return {};
    out = Object.fromEntries(entries.map(([k], i) => [k, lines[i].trim()]));
  } catch {
    return {};
  }

  // 3. Write through. Fire-and-forget: a cache that fails to save costs one more
  // translation later, and must never break the render.
  sb.from('message_translations')
    .upsert({ message_id: messageId, direction, translated_text: JSON.stringify(out) },
      { onConflict: 'message_id,direction' })
    .then(undefined, () => {});

  return out;
}
